import React from 'react';
import classNames from 'classnames';
import {
  FDBodyText,
  FDEmphasisSurface,
  FDHeadlineText,
  FDPaper,
  FDPrimaryActionButton,
  FDStack,
} from '@facet-wealth/design';
import MetaControl from 'Components/UI/Meta/MetaControl';
import { buildFacetDesignOverride, overrideReadOnly } from 'Packages/FacetRest/FacetDesignOverride';
import PropTypes from 'prop-types';
import fr from 'facet-rest';
import { usePageContentContext } from 'Views/Page/Contexts/PageContentContext';
import useMetaLayoutContext from 'Components/UI/Meta/MetaForm/Components/MetaLayoutContext';
import useResource from 'Hooks/useResource';
import { dataAttrs, DataValueMap, testAttr } from 'Helpers/Testing';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { Richtext } from 'Packages/FacetDesign/Components/Richtext';
import { ProductHeader } from './ProductHeader';
import { ProductTags } from './ProductTags';
import { ProductDocument } from './ProductDocument';

export const productMetaOverrideSet = buildFacetDesignOverride()
  .nameOverride(['title'], {
    _component: () => FDHeadlineText,
  }, overrideReadOnly)
  .override({ name: 'firstYearAmount', hint: 'richtext' }, {
    _component: () => Richtext,
    _customProps: { size: 'md', align: 'end', Component: FDHeadlineText },
  }, overrideReadOnly)
  .nameOverride(['recurringAmount', 'recurringAmountNote'], {
    _component: () => FDBodyText,
    _customProps: { align: 'end', size: 'sm' },
  }, overrideReadOnly)
  .nameOverride('tags', {
    _component: () => ProductTags,
    _spreadPropNames: ['resource'],
  }, overrideReadOnly)
  .nameOverride('document', {
    _component: () => ProductDocument,
  }, overrideReadOnly);


const useStyles = buildUseStyles('ProductActions', ((theme) => ({
  selectedButton: {
    '& > .FD-MuiButton-root': {
      '&:hover': {
        backgroundColor: theme.palette.facet.mint30p,
        color: theme.palette.facet.mint100p,
      },
      backgroundColor: theme.palette.facet.mint30p,
      color: theme.palette.facet.mint100p,
    },
  },
})));


export const Product = ({
  resource: productResourceProp,
  variant,
}) => {
  const { pageContentResource, loadPageContentLink } = usePageContentContext();
  const { handleChange } = useMetaLayoutContext();
  const { resource: productResource, setAttribute } = useResource(productResourceProp);
  const classes = useStyles();

  const isSelected = pageContentResource.attr('product') === productResource.id();

  let Component = (isSelected) ? FDEmphasisSurface : FDPaper;

  // temporary hack for Tax Prep styling until EP product card is redesigned
  if (variant === 'tax' && productResource.attr('product') === 'full-service') {
    Component = FDEmphasisSurface;
  }

  let label = isSelected ? 'Selected' : 'Select this plan';
  if (productResource.attr('primaryActionLabel')) {
    label = productResource.attr('primaryActionLabel');
  }

  return (
    <Component
      {...DataValueMap.init(`product-${productResource.attr('product')}${isSelected ? '-selected' : ''}`).build()}
    >
      <FDStack direction={'column'} justifyContent={'space-between'} spacing={2} padding={3} maxWidth={'500px'}>
        <FDStack height={'100%'} direction={'column'} justifyContent={'flex-start'} spacing={2}>
          <MetaControl resource={productResource} attrName={'tags'} metaOverrideSet={productMetaOverrideSet} />
          <ProductHeader metaOverrideSet={productMetaOverrideSet} productResource={productResource} />
          <MetaControl
            resource={productResource}
            attrName={'document'}
            metaOverrideSet={productMetaOverrideSet}
            onChange={(val) => {
              setAttribute('document', val);
              handleChange('document', val);
            }}
            CustomProps={{
              isSelected,
            }}
          />
          <MetaControl resource={productResource} attrName={'description'} metaOverrideSet={productMetaOverrideSet} />
        </FDStack>
        <div className={classNames({
          [classes.selectedButton]: isSelected,
        })}
        >
          <FDPrimaryActionButton
            {...dataAttrs('product-selection-btn', {
              [testAttr.dataValue]: isSelected,
            })}
            color={isSelected ? 'success' : 'primary'}
            icon={isSelected ? 'check' : undefined}
            label={label}
            onClick={productResource.withRelationship(
              fr.relationships.actionLink.name,
              (rel) => () => loadPageContentLink(rel.link('related')),
              () => () => {
                if (!isSelected) {
                  handleChange('product', productResource.id());
                  handleChange('document', productResource.attr('document'));
                }
              },
            )}
          />
        </div>
      </FDStack>
    </Component>
  );
};

Product.defaultProps = {};

Product.propTypes = {
  resource: PropTypes.instanceOf(fr.Resource).isRequired,
};
